import React, { useMemo } from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { Vehicle, Sale } from '../../types';
import {
  buildContratoVendaParagraphs,
  buildContratoVendaVariables,
  chunkContratoParagraphsForPdf,
  ContratoParagraph,
} from './contratoVendaUtils';
import { CONTRATO_VENDA_TIMBRADO } from './contratoVendaConfig';

const pad = CONTRATO_VENDA_TIMBRADO.paddingPdf;
const PARAGRAPHS_PER_PAGE = 20;

const styles = StyleSheet.create({
  page: {
    position: 'relative',
  },
  timbrado: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'fill',
  },
  content: {
    position: 'relative',
    paddingTop: pad.top,
    paddingBottom: pad.bottom,
    paddingLeft: pad.left,
    paddingRight: pad.right,
    fontSize: 8,
    fontFamily: 'Helvetica',
    lineHeight: 1.38,
  },
  title: {
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 10,
  },
  heading: {
    fontWeight: 'bold',
    marginTop: 5,
    marginBottom: 2,
  },
  paragraph: {
    textAlign: 'justify',
    marginBottom: 4,
  },
  signature: {
    marginTop: 6,
    marginBottom: 2,
  },
  footerNote: {
    marginTop: 6,
    fontSize: 7,
    color: '#444444',
  },
});

interface ContratoVendaPDFProps {
  vehicle: Vehicle;
  sale: Sale;
  dataEmissao?: string;
  /** Data URL base64 do timbrado (obrigatório) */
  timbradoSrc: string;
}

const renderPdfParagraph = (paragraph: ContratoParagraph, index: number) => {
  if (paragraph.kind === 'title') {
    return (
      <Text key={index} style={styles.title}>
        {paragraph.text}
      </Text>
    );
  }

  if (paragraph.kind === 'heading') {
    return (
      <Text key={index} style={styles.heading}>
        {paragraph.text}
      </Text>
    );
  }

  if (paragraph.kind === 'signature') {
    return (
      <Text key={index} style={styles.signature}>
        {paragraph.text}
      </Text>
    );
  }

  return (
    <Text key={index} style={styles.paragraph}>
      {paragraph.text}
    </Text>
  );
};

const ContratoVendaPDFDoc: React.FC<ContratoVendaPDFProps> = ({
  vehicle,
  sale,
  dataEmissao = new Date().toISOString().split('T')[0],
  timbradoSrc,
}) => {
  const pages = useMemo(() => {
    const vars = buildContratoVendaVariables(vehicle, sale, dataEmissao);
    const paragraphs = buildContratoVendaParagraphs(vars);
    return chunkContratoParagraphsForPdf(paragraphs, PARAGRAPHS_PER_PAGE);
  }, [vehicle, sale, dataEmissao]);

  return (
    <Document title="Contrato Particular de Compra e Venda de Veículo Seminovo">
      {pages.map((chunk, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          <Image src={timbradoSrc} style={styles.timbrado} fixed />
          <View style={styles.content}>
            {chunk.map((paragraph, idx) => renderPdfParagraph(paragraph, idx))}
            {pageIndex === pages.length - 1 && (
              <Text style={styles.footerNote}>
                Documento gerado automaticamente. Recomenda-se revisão pelo setor jurídico antes do uso.
              </Text>
            )}
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default ContratoVendaPDFDoc;
